import { styled } from '@mui/material/styles';
import type { Virtualizer } from '@tanstack/react-virtual';
import { useContext } from 'react';
import {
  ColRowHighlightContext,
  useSwimlaneRootProps,
  useSwimlaneSlot,
} from '@/components/ui/swimlane/context';
import {
  CARD_WIDTH,
  COL_ROW_HIGHLIGHT_COLOR,
  COLUMN_HEADER_HEIGHT,
  COMPACT_CARD_WIDTH,
  GRID_GAP,
} from '@/components/ui/swimlane/layout/constants';

interface IColumnHeaders {
  columnVirtualizer: Virtualizer<HTMLDivElement, Element>;
}

export function ColumnHeaders({ columnVirtualizer }: IColumnHeaders) {
  const { component: SlotColumnHeader, props: columnHeaderSlotProps } =
    useSwimlaneSlot('columnHeader');
  const {
    columnAxisLabelText,
    columnHeaders,
    isCompact,
    isHeadersSticky,
    isPngPublicationTemplate,
    isPdfExportTemplate,
    backgroundColor,
  } = useSwimlaneRootProps();
  const { colHighlightIndex, setColHighlightIndex } = useContext(ColRowHighlightContext);

  // Disable sticky headers for PDF and PNG export templates
  const effectiveIsHeadersSticky =
    isHeadersSticky && !isPngPublicationTemplate && !isPdfExportTemplate;

  const hasAxisLabelColumn = typeof columnAxisLabelText === 'string' && effectiveIsHeadersSticky;

  return (
    <Container
      data-export="column-header-container"
      isHeadersSticky={effectiveIsHeadersSticky}
      backgroundColor={backgroundColor}
      style={{
        gridArea: 'colH',
        width: `${columnVirtualizer.getTotalSize()}px`,
        top: `${hasAxisLabelColumn ? `${COLUMN_HEADER_HEIGHT.value + GRID_GAP.value}px` : '0px'}`,
      }}
    >
      {columnVirtualizer.getVirtualItems().map((virtualColumn) => {
        const columnHeaderData = columnHeaders[virtualColumn.index];
        const isColumnHighlighted = colHighlightIndex === virtualColumn.index;
        // styles for highlighting column header
        const columnHighLightStyles = {
          backgroundColor: COL_ROW_HIGHLIGHT_COLOR,
          outline: `6px solid ${COL_ROW_HIGHLIGHT_COLOR}`,
          borderTopLeftRadius: '6px',
          borderTopRightRadius: '6px',
        };

        return (
          <ColumnHeaderContainer
            key={virtualColumn.index}
            style={{
              width: isCompact ? COMPACT_CARD_WIDTH.label : CARD_WIDTH.label,
              transform: `translateX(${virtualColumn.start}px)`,
              ...(isColumnHighlighted ? columnHighLightStyles : {}),
              backgroundColor,
            }}
            onMouseEnter={() => setColHighlightIndex(virtualColumn.index)}
            onMouseLeave={() => setColHighlightIndex(-1)}
          >
            {columnHeaderData && (
              <SlotColumnHeader
                {...columnHeaderSlotProps}
                {...columnHeaderData}
                id={String(columnHeaders[virtualColumn.index]?.id)}
              />
            )}
          </ColumnHeaderContainer>
        );
      })}
    </Container>
  );
}

const ColumnHeaderContainer = styled('div')({
  position: 'absolute',
  height: '32px',
});

const Container = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isHeadersSticky' && prop !== 'backgroundColor',
})<{ isHeadersSticky: boolean; backgroundColor?: string }>(
  ({ isHeadersSticky, backgroundColor }) => ({
    backgroundColor: backgroundColor || '#fff',
    position: isHeadersSticky ? 'sticky' : 'relative',
    zIndex: 10,
  }),
);
